import firebase from 'firebase/app'
import 'firebase/auth'

/**
 * Single active session per user, globally. The session id is held in localStorage, which gives
 * exactly the scoping the rule needs: tabs in one browser profile share a session because they
 * are one client, while a second browser, a second profile and a private window each get their
 * own storage and therefore each have to claim the session for themselves.
 */
const SESSION_ID_KEY = 'sessionId'

export const SESSION_EXISTS_CODE = 'SESSION_EXISTS'

export const SESSION_TERMINATED_CODE = 'SESSION_TERMINATED'

export const SESSION_TERMINATED_MESSAGE = 'You have been signed out because your account was used to sign in on another device.'

/** Wording is fixed by the ticket and is reproduced verbatim. */
export const SESSION_CONFLICT_MESSAGE = 'Your account is already logged in elsewhere. Only one active session is allowed at a time. If you continue, the other session will be logged out.'

export const ACCOUNT_DISABLED_MESSAGE = 'Your account has been disabled. Please connect with the admin to reactivate your access.'

export const SESSION_IDLE_TIMEOUT_CODE = 'SESSION_IDLE_TIMEOUT'

/** Wording is fixed by the ticket and is reproduced verbatim. */
export const SESSION_IDLE_TIMEOUT_MESSAGE = 'Your session has expired due to inactivity. Please sign in again.'

export const IDLE_TIMEOUT_REASON = 'idle-timeout'

const SIGN_OUT_REASON_PARAM = 'reason'

const SIGN_OUT_REASON_KEY = 'signOutReason'

/** Why a signed in user was thrown back to sign-in, and what the sign-in screen says about it. */
const SIGN_OUT_REASONS = {
	'session-terminated': SESSION_TERMINATED_MESSAGE,
	'account-disabled': ACCOUNT_DISABLED_MESSAGE,
	[IDLE_TIMEOUT_REASON]: SESSION_IDLE_TIMEOUT_MESSAGE
}

/**
 * True while this device is establishing or re-establishing its own session — claiming one at
 * startup, or taking it from another device, which revokes every token this user holds until the
 * custom token that comes back is redeemed. Requests that fail in those windows are this device
 * mid-handshake, not a device that has been displaced, and must not be read as the session ending.
 */
let sessionHandshake = false

export const beginSessionHandshake = () => { sessionHandshake = true }

export const endSessionHandshake = () => { sessionHandshake = false }

export const isSessionHandshake = () => sessionHandshake

/**
 * Nothing on this device works any more. Tear the session down and return to sign-in carrying the
 * reason, rather than leaving the user on a page whose every request will now fail.
 */
export const endSession = reason => {
	clearSessionId()
	// Recorded before signing out rather than after. Signing out is itself what makes the route
	// guards send the user to sign-in, and that redirect can land before the one below runs, on a
	// sign-in screen that has not been told anything — which is how a forced logout could arrive
	// with no message at all. Recording the reason first means whichever navigation gets there
	// first finds it already waiting.
	recordSessionEnded(reason)
	firebase.auth().signOut().finally(() => {
		redirectToSignIn(reason)
	})
}

/**
 * Ends the session on this device and returns to sign-in explaining why.
 *
 * The reason is handed over twice on purpose. The URL parameter is what a hard navigation carries,
 * and is the only channel left when storage is unavailable. sessionStorage is what survives any
 * further hop, such as a guard bouncing the user between /login and / while Firebase is still
 * settling, which the parameter would not survive.
 */
export const redirectToSignIn = reason => {
	storeSignOutReason(reason)
	window.location.href = `/login?${SIGN_OUT_REASON_PARAM}=${reason}`
}

const storeSignOutReason = reason => {
	try {
		window.sessionStorage.setItem(SIGN_OUT_REASON_KEY, reason)
	} catch (e) {
		// Storage unavailable (private browsing). The URL parameter still carries the reason.
	}
}

/**
 * The message the sign-in screen shows, once a reason has arrived by either channel.
 *
 * It is remembered for as long as the page lives instead of being handed to the first caller and
 * forgotten. The sign-in screen mounts more than once within a single page load — a guard bouncing
 * the user between /login and /, or the application swapping its loader in while it settles — and
 * a one-shot reason left every mount after the first with nothing to show, which is how the notice
 * could disappear on its own. A genuine refresh starts a new page and so clears this, which is
 * where the message is meant to go.
 */
let signOutMessage = null

/**
 * The message for however the reason arrived, or null if the user simply came to sign in. Both
 * storage channels are cleared as they are read so the message belongs to this page load only.
 */
export const consumeSignOutReason = () => {
	const params = new URLSearchParams(window.location.search)
	const fromUrl = params.get(SIGN_OUT_REASON_PARAM)
	if (fromUrl) {
		params.delete(SIGN_OUT_REASON_PARAM)
		const query = params.toString()
		window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
	}
	let fromStorage = null
	try {
		fromStorage = window.sessionStorage.getItem(SIGN_OUT_REASON_KEY)
		window.sessionStorage.removeItem(SIGN_OUT_REASON_KEY)
	} catch (e) {
		// Storage unavailable. Whatever the URL carried is all there is.
	}
	// Nothing arriving this time leaves whatever already arrived in place, so a remount keeps
	// showing the message rather than clearing the screen.
	signOutMessage = SIGN_OUT_REASONS[fromUrl || fromStorage] || signOutMessage
	return signOutMessage
}

/**
 * Forgets the notice once the user has acted on it by starting another sign-in, so it cannot
 * outlive what it was explaining and reappear over a later, unrelated return to this screen.
 */
export const clearSignOutReason = () => {
	signOutMessage = null
}

const signOutReasonListeners = new Set()

/**
 * Records why the session ended, without navigating anywhere itself.
 *
 * Not every ending is one this application notices first. Claiming a session revokes the user's
 * tokens everywhere, so on a displaced device it is Firebase's own token refresh — at start-up,
 * before a single request of ours has been made — that fails and signs the user out, and the route
 * guards send them to sign-in on their own. Recording the reason separately from the redirect
 * means that ending can be explained too, whoever ends up performing the navigation.
 */
export const recordSessionEnded = reason => {
	storeSignOutReason(reason)
	const message = SIGN_OUT_REASONS[reason]
	if (!message) return
	signOutMessage = message
	// The sign-in screen may already be up: the guards can get the user there before the reason
	// is known, so it is told rather than left with whatever it read on mount.
	signOutReasonListeners.forEach(listener => listener(signOutMessage))
}

export const subscribeToSignOutReason = listener => {
	signOutReasonListeners.add(listener)
	return () => { signOutReasonListeners.delete(listener) }
}

/**
 * The desktop label application is this same Next.js app wrapped in Electron, built from a branch
 * that cherry-picks from web. Detecting it at runtime keeps the agent correct there without the
 * branch having to carry an edit to this file.
 */
export const isDesktopApp = () =>
	typeof window !== 'undefined' && /Electron\//.test(window.navigator.userAgent)

/**
 * Descriptive only — the server never keys the single-session rule on it. It decides what the
 * other device is told it is competing with.
 */
export const getSessionAgent = () => isDesktopApp() ? 'DESKTOP' : 'WEB'

export const getSessionId = () => {
	try {
		return window.localStorage.getItem(SESSION_ID_KEY)
	} catch (e) {
		return null
	}
}

export const setSessionId = sessionId => {
	try {
		window.localStorage.setItem(SESSION_ID_KEY, sessionId)
	} catch (e) {
		// Storage unavailable. The session still exists server side; this device just cannot prove
		// which one is its own, so it will be challenged again on the next sign-in.
	}
}

export const clearSessionId = () => {
	try {
		window.localStorage.removeItem(SESSION_ID_KEY)
	} catch (e) {
		// Nothing to clear.
	}
}

/**
 * Best-effort human readable device name for the confirmation dialog, e.g. "Chrome on Windows".
 * Descriptive only — it never takes part in deciding whether a session may be claimed.
 */
export const getDeviceLabel = () => {
	if (typeof window === 'undefined') return null
	const ua = window.navigator.userAgent
	// Electron reports Chrome in its user agent, so without this the label application would
	// describe itself as a browser to whoever it displaces.
	const browser = isDesktopApp() ? 'Nuport Desktop'
		: /Edg\//.test(ua) ? 'Edge'
			: /OPR\//.test(ua) ? 'Opera'
				: /Chrome\//.test(ua) ? 'Chrome'
					: /Safari\//.test(ua) ? 'Safari'
						: /Firefox\//.test(ua) ? 'Firefox'
							: 'Browser'
	const platform = /Windows/.test(ua) ? 'Windows'
		: /Mac OS X/.test(ua) ? 'macOS'
			: /Android/.test(ua) ? 'Android'
				: /(iPhone|iPad)/.test(ua) ? 'iOS'
					: /Linux/.test(ua) ? 'Linux'
						: null
	return platform ? `${browser} on ${platform}` : browser
}

/**
 * Firebase authenticating is not the end of signing in: this device still has to claim the single
 * session, and may first have to ask the user whether to displace another device. Until that
 * settles the sign-in screen stays put, so the conflict dialog is answered there rather than over
 * a dashboard this device may end up not being entitled to.
 */
let sessionEstablished = false

const sessionStateListeners = new Set()

export const isSessionEstablished = () => sessionEstablished

export const setSessionEstablished = established => {
	if (sessionEstablished === established) return
	sessionEstablished = established
	sessionStateListeners.forEach(listener => listener(sessionEstablished))
}

export const subscribeToSessionState = listener => {
	sessionStateListeners.add(listener)
	return () => { sessionStateListeners.delete(listener) }
}
